<template>
  <div class="lottie-wrapper" @mouseenter="hovered = true" @mouseleave="hovered = false" @focusin="hovered = true" @focusout="hovered = false">
    <div v-if="props.caption" class="caption" role="tooltip" :aria-hidden="!hovered">{{ props.caption }}</div>
    <button
      :class="['lottie-button', props.buttonClass]"
      :disabled="props.disabledWhilePlaying && isPlaying"
      :title="props.caption ? '' : props.title"
      :aria-label="props.ariaLabel"
      @click="onClick"
    >
    <template v-if="isAvailable">
      <lottie-player
        :src="src"
        background="transparent"
        :style="computedStyle"
        :speed="speed"
        :mode="mode"
        v-bind="playerAttrs"
        ref="player"
        @complete="onComplete"
      ></lottie-player>
      </template>
      <template v-else>
      <font-awesome-icon :icon="['fas', fallbackIcon]" />
    </template>
    </button>
  </div>
</template>

<script setup>
/* global defineProps, defineEmits */
import { ref, onMounted, computed } from 'vue';

const props = defineProps({
  src: { type: String, required: true },
  size: { type: [Number, String], default: 36 },
  autoplay: { type: Boolean, default: false },
  loop: { type: Boolean, default: false },
  clickToPlay: { type: Boolean, default: false },
  disabledWhilePlaying: { type: Boolean, default: true },
  fallbackIcon: { type: String, default: 'refresh' },
  title: { type: String, default: '' },
  ariaLabel: { type: String, default: '' },
  caption: { type: String, default: '' },
  mode: { type: String, default: 'normal' },
  speed: { type: Number, default: 1 }
  ,buttonClass: { type: String, default: '' }
});

const emit = defineEmits(['activated', 'complete', 'error', 'play']);

const player = ref(null);
const isAvailable = ref(true);
const isPlaying = ref(false);
const hovered = ref(false);

const computedStyle = computed(() => {
  const size = typeof props.size === 'number' ? `${props.size}px` : props.size;
  return { width: size, height: size, display: 'block' };
});

const playerAttrs = computed(() => {
  // If clickToPlay is true, don't set autoplay; else use props
  return {
    autoplay: !props.clickToPlay && props.autoplay,
    loop: props.loop
  };
});

async function checkAssets() {
  try {
    const r = await fetch(props.src);
    if (!r.ok) { isAvailable.value = false; return }
    const json = await r.json();
    const assets = json.assets || [];
    for (const a of assets) {
      const path = `/img/${(a.u || '')}${a.p || ''}`.replace(/\\/g, '/');
      const rr = await fetch(path);
      if (!rr.ok) { isAvailable.value = false; return }
    }
    isAvailable.value = true;
  } catch (e) {
    isAvailable.value = false;
  }
}

onMounted(async () => {
  await checkAssets();
  // If autoplay requested and not clickToPlay
  if (!props.clickToPlay && props.autoplay && isAvailable.value && player.value) {
    try { player.value.play(); isPlaying.value = true; emit('play'); } catch(e){ console.warn(e) }
  }
  // attach complete event if available
  if (player.value) {
    try {
      player.value.addEventListener('complete', () => {
        isPlaying.value = false;
        emit('complete');
      });
    } catch (e) {
      console.warn(e);
    }
  }
});

function onComplete() {
  isPlaying.value = false;
  try { player.value && player.value.pause(); } catch(e) { console.warn(e) }
  emit('complete');
}

async function onClick(e) {
  // If lottie is available and clickToPlay is true, play it once
  if (isAvailable.value && props.clickToPlay && player.value && !isPlaying.value) {
    try {
      isPlaying.value = true;
      player.value.stop();
      player.value.play();
      emit('play');
    } catch (err) {
      isPlaying.value = false;
      emit('error', err);
    }
  }
  // Emit activated so parent can do their refresh
  emit('activated', e);
}

// Hover/focus handled inline in template to avoid unused function lint warnings
</script>

<style scoped>
.lottie-wrapper { position:relative; display:inline-block }
.caption{ position:absolute; bottom:100%; left:50%; transform:translateX(-50%) translateY(6px); background:var(--background-color); color:var(--text-color-primary); padding:6px 8px; border-radius:6px; font-size:12px; white-space:nowrap; box-shadow:0 6px 18px rgba(0,0,0,0.06); opacity:0; visibility:hidden; pointer-events:none; transition:opacity .14s ease, transform .14s ease }
.lottie-wrapper:hover .caption, .lottie-wrapper:focus-within .caption{ opacity:1; visibility:visible; transform:translateX(-50%) translateY(0) }
.lottie-button { border: none; background: transparent; padding: 6px; display:inline-flex; align-items:center; justify-content:center; cursor:pointer }
.lottie-button[disabled]{ pointer-events:none; opacity:0.7 }
</style>
